// Palm recipe library v1: expands a per-frond recipe into leaflet plates, deterministically, and casts them into the sun's
// shadow map only (they are never drawn in the main pass; the frozen records still draw the visible tree).
//
// This is the client half of a written spec (palm_lib.py is the reference). Everything random comes from a 32-bit integer hash,
// so two clients given the same recipe and seed produce the same plates. All geometry here is in SIMULATION axes (x, y, z up,
// metres); the caster converts to three.js axes (x, z, -y) when it fills the instance buffers.
import * as THREE from 'three';

export interface FrondRecipe {
  base: [number, number, number];   // where the frond leaves the trunk
  phi: number;                      // azimuth of the frond's vertical plane, radians
  th0: number;                      // launch angle above horizontal, radians
  kappa: number;                    // bend per metre of arc length, radians (positive bends downward)
  n: number;                        // rachis segments
  area: number;                     // leaf area the sim holds on this frond, m^2
}

export interface PalmParams {
  step: number;       // rachis segment length (internode_length * falloff)
  pitch: number;      // spacing of leaflet pairs along the rachis
  reach: number;      // leaflet length before jitter
  hangDeg: number;    // how far leaflets hang below horizontal
  rollDeg: number;    // random twist about the leaflet axis
  tipExt: number;     // the leaflets run this far past the last rachis cell, like the sim's foliage does
  c: number;          // plate surface area / the sim's disc area, so that the day-averaged shade matches the sim
}

export const PALM_V1: PalmParams = { step: 0.187, pitch: 0.05, reach: 0.40, hangDeg: 40, rollDeg: 20, tipExt: 0.10, c: 1.74 };

// ---- portable hash ---------------------------------------------------------------------------------------------------
export function hash32(x: number): number {
  x >>>= 0;
  x ^= x >>> 16; x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15; x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}
/** 24-bit uniform in [0, 1) from (seed, idx, chan). */
export function unit(seed: number, idx: number, chan: number): number {
  const k = (Math.imul(seed, 0x9E3779B1) ^ Math.imul(idx, 0x85EBCA6B) ^ Math.imul(chan, 0xC2B2AE35)) >>> 0;
  return (hash32(k) >>> 8) / 16777216;
}
export function frondSeed(plantSeed: number, fi: number): number {
  return hash32((plantSeed ^ Math.imul(fi + 1, 0x27D4EB2F)) >>> 0);
}

// ---- expansion -------------------------------------------------------------------------------------------------------
export const PLATE_FLOATS = 11;   // centre(3) length axis(3) width axis(3) length width

export interface Expanded { count: number; w: number; data: Float64Array }

/** One frond into plates. data holds, per plate, centre xyz, length axis xyz (unit), width axis xyz (unit), length, then the
 *  frond's single plate width. Plates run from the rachis outward: the centre sits half a length along the axis. */
export function expandFrond(rec: FrondRecipe, seed: number, p: PalmParams = PALM_V1): Expanded {
  const L = rec.n * p.step, Lx = L + p.tipExt;
  const ns = Math.max(2, Math.floor(Lx / p.pitch + 0.5));
  const cph = Math.cos(rec.phi), sph = Math.sin(rec.phi), th0 = rec.th0, k = rec.kappa;
  const hang = p.hangDeg * Math.PI / 180, rollMax = p.rollDeg * Math.PI / 180;
  const count = ns * 2;
  const data = new Float64Array(count * PLATE_FLOATS);
  let sumEll = 0;
  for (let j = 0; j < ns; j++) {
    const s = (j + 0.5) * (Lx / ns);
    const th = th0 - k * s;
    let hx: number, z: number;
    if (Math.abs(k) < 1e-9) { hx = s * Math.cos(th0); z = s * Math.sin(th0); }
    else { hx = (Math.sin(th0) - Math.sin(th)) / k; z = (Math.cos(th) - Math.cos(th0)) / k; }
    const Px = rec.base[0] + hx * cph, Py = rec.base[1] + hx * sph, Pz = rec.base[2] + z;
    const Tx = Math.cos(th) * cph, Ty = Math.cos(th) * sph, Tz = Math.sin(th);
    // side = T x up, normalised
    let sx = Ty, sy = -Tx; const sl = Math.hypot(sx, sy) + 1e-12; sx /= sl; sy /= sl;
    const ell = p.reach * (0.7 + 0.6 * unit(seed, j, 1));
    const jg = hang * (0.6 + 0.8 * unit(seed, j, 2));
    const cg = Math.cos(jg), sg = Math.sin(jg);
    for (let side = 0; side < 2; side++) {
      const sgn = side === 0 ? 1 : -1;
      let qx = cg * sgn * sx, qy = cg * sgn * sy, qz = -sg;
      const ql = Math.hypot(qx, qy, qz); qx /= ql; qy /= ql; qz /= ql;
      const roll = rollMax * (2 * unit(seed, j + (side === 0 ? 0 : ns), 3) - 1);
      const cr = Math.cos(roll), sr = Math.sin(roll);
      // q x T
      const cx = qy * Tz - qz * Ty, cy = qz * Tx - qx * Tz, cz = qx * Ty - qy * Tx;
      const o = (side * ns + j) * PLATE_FLOATS;
      data[o] = Px + 0.5 * ell * qx; data[o + 1] = Py + 0.5 * ell * qy; data[o + 2] = Pz + 0.5 * ell * qz;
      data[o + 3] = qx; data[o + 4] = qy; data[o + 5] = qz;
      data[o + 6] = cr * Tx + sr * cx; data[o + 7] = cr * Ty + sr * cy; data[o + 8] = cr * Tz + sr * cz;
      data[o + 9] = ell;
      sumEll += ell;
    }
  }
  const w = rec.area * p.c / sumEll;
  for (let i = 0; i < count; i++) data[i * PLATE_FLOATS + 10] = w;
  return { count, w, data };
}

// ---- the broadcast server's message ------------------------------------------------------------------------------------
// {"type":"palm_recipes","lib":"palm","v":1,"palms":N,"data":base64}. The data is little-endian: u16 palms; per palm u32 plant id
// (the seed) and u16 fronds; per frond 3 x f32 base (simulation axes), u16 azimuth, i16 launch angle, i16 bend, u8 segments,
// u16 leaf area. The scale factors below are the server's (palm_recipes.py) and must stay equal to them.
export const PALM_RECIPE_VERSION = 1;
const Q_ANGLE = 2 * Math.PI / 65536, Q_TH0 = 1e-4, Q_KAPPA = 1e-4, Q_AREA = 1e-4;
export interface PalmRecipesMessage { type: 'palm_recipes'; lib: string; v: number; palms: number; data: string }

export function decodePalmRecipes(data: string): { seed: number; fronds: FrondRecipe[] }[] {
  const bin = atob(data), u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  const v = new DataView(u8.buffer);
  let off = 0;
  const n = v.getUint16(off, true); off += 2;
  const palms: { seed: number; fronds: FrondRecipe[] }[] = [];
  for (let p = 0; p < n; p++) {
    const seed = v.getUint32(off, true), nf = v.getUint16(off + 4, true); off += 6;
    const fronds: FrondRecipe[] = [];
    for (let f = 0; f < nf; f++) {
      fronds.push({
        base: [v.getFloat32(off, true), v.getFloat32(off + 4, true), v.getFloat32(off + 8, true)],
        phi: v.getUint16(off + 12, true) * Q_ANGLE, th0: v.getInt16(off + 14, true) * Q_TH0, kappa: v.getInt16(off + 16, true) * Q_KAPPA,
        n: v.getUint8(off + 18), area: v.getUint16(off + 19, true) * Q_AREA,
      });
      off += 21;
    }
    palms.push({ seed, fronds });
  }
  return palms;
}

// ---- shadow-only casters -----------------------------------------------------------------------------------------------
const TIP_TAPER = 0.2;            // a leaflet's tip is this fraction of its base width

/** Leaflet plates for any number of palms, drawn only into the sun's shadow map. */
export class PalmShadowCasters {
  readonly mesh: THREE.Mesh;
  private geo = new THREE.InstancedBufferGeometry();
  private depth: THREE.MeshDepthMaterial;
  private on = true;
  private n = 0;
  private cap = 0;
  private aC!: THREE.InstancedBufferAttribute; private aQ!: THREE.InstancedBufferAttribute;
  private aT!: THREE.InstancedBufferAttribute; private aS!: THREE.InstancedBufferAttribute;

  constructor(scene: THREE.Scene, capacity = 60000) {
    const base = new THREE.BufferGeometry();
    // u along the leaflet (-0.5 at the rachis, +0.5 at the tip), v across it
    base.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, -0.5, 0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0]), 3));
    base.setIndex([0, 2, 1, 1, 2, 3]);
    this.geo.index = base.index; this.geo.setAttribute('position', base.getAttribute('position'));
    this.depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.DoubleSide });
    this.depth.onBeforeCompile = (sh) => {
      sh.vertexShader = sh.vertexShader
        .replace('void main() {', 'attribute vec3 iC; attribute vec3 iQ; attribute vec3 iT; attribute vec2 iS;\nvoid main() {')
        .replace('#include <begin_vertex>',
          `float tu = position.x + 0.5;
           vec3 transformed = iC + iQ * (position.x * iS.x) + iT * (position.y * iS.y * mix(1.0, ${TIP_TAPER.toFixed(3)}, tu));`);
    };
    this.depth.customProgramCacheKey = () => 'palm-leaflet-depth-v1';
    // The main pass draws nothing: no colour, no depth, and (below) zero instances. The shadow pass sets the real count.
    this.mesh = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }));
    this.mesh.frustumCulled = false; this.mesh.castShadow = true; this.mesh.receiveShadow = false;
    this.mesh.customDepthMaterial = this.depth;
    this.mesh.onBeforeShadow = () => { this.geo.instanceCount = this.on ? this.n : 0; };
    this.mesh.onAfterShadow = () => { this.geo.instanceCount = 0; };
    this.geo.instanceCount = 0;
    this.alloc(capacity);
    scene.add(this.mesh);
  }

  private alloc(cap: number) {
    this.cap = cap;
    const mk = (size: number) => { const a = new THREE.InstancedBufferAttribute(new Float32Array(cap * size), size); a.setUsage(THREE.DynamicDrawUsage); return a; };
    this.aC = mk(3); this.aQ = mk(3); this.aT = mk(3); this.aS = mk(2);
    this.geo.setAttribute('iC', this.aC); this.geo.setAttribute('iQ', this.aQ); this.geo.setAttribute('iT', this.aT); this.geo.setAttribute('iS', this.aS);
  }

  setEnabled(v: boolean) { this.on = v; }

  /** Take the broadcast server's recipes. Returns the number of palms, or -1 when the message is from a library version this client lacks. */
  load(msg: PalmRecipesMessage): number {
    if (msg.lib !== 'palm' || msg.v !== PALM_RECIPE_VERSION) return -1;
    const palms = decodePalmRecipes(msg.data);
    this.setPalms(palms);
    return palms.length;
  }
  get plates() { return this.n; }
  get triangles() { return this.n * 2; }

  /** Replace every plate with those of the given palms. */
  setPalms(palms: { seed: number; fronds: FrondRecipe[] }[], params: PalmParams = PALM_V1) {
    const all: Expanded[] = [];
    let total = 0;
    for (const pm of palms) pm.fronds.forEach((f, fi) => { const e = expandFrond(f, frondSeed(pm.seed, fi), params); all.push(e); total += e.count; });
    if (total > this.cap) this.alloc(Math.ceil(total * 1.25));
    let i = 0;
    for (const e of all) {
      for (let p = 0; p < e.count; p++, i++) {
        const o = p * PLATE_FLOATS, d = e.data;
        // simulation (x, y, z up) to three.js (x, z, -y)
        this.aC.setXYZ(i, d[o], d[o + 2], -d[o + 1]);
        this.aQ.setXYZ(i, d[o + 3], d[o + 5], -d[o + 4]);
        this.aT.setXYZ(i, d[o + 6], d[o + 8], -d[o + 7]);
        this.aS.setXY(i, d[o + 9], e.w);
      }
    }
    this.n = i;
    for (const a of [this.aC, this.aQ, this.aT, this.aS]) a.needsUpdate = true;
  }

  dispose() { this.geo.dispose(); this.depth.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.removeFromParent(); }
}
